import type { DrawerRootProps } from "@base-ui/react/drawer"
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import React from "react"

import { useResizablePanel } from "#React/hooks"
import Icon from "#React/ui/Icon/Icon"
import ResizeHandle from "#React/ui/ResizeHandle"
import { cn } from "#Utils/cn"

type Direction = "right" | "left" | "top" | "bottom"
type Size = "sm" | "md" | "lg" | "xl" | "full"

const SWIPE_DIRECTION_MAP: Record<Direction, DrawerRootProps["swipeDirection"]> = {
  right: "right",
  left: "left",
  top: "up",
  bottom: "down",
}

const POPUP_BASE_CLASSES =
  "fixed z-50 pointer-events-auto bg-surface-default shadow-xl outline-none transition-transform duration-300 ease-out"

// Only horizontal drawers can be resized; the handle sits on the inner edge.
const DEFAULT_RESIZE_WIDTH = 560
const DEFAULT_RESIZE_MIN_WIDTH = 420
const DEFAULT_RESIZE_MAX_WIDTH = 1100
const RESIZE_VIEWPORT_PADDING = 16

const POPUP_DIRECTION_CLASSES: Record<Direction, string> = {
  right:
    "top-0 right-0 h-full border-l border-edge-default translate-x-0 data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full",
  left: "top-0 left-0 h-full border-r border-edge-default translate-x-0 data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full",
  bottom:
    "bottom-0 left-0 w-full border-t border-edge-default translate-y-0 data-[ending-style]:translate-y-full data-[starting-style]:translate-y-full",
  top: "top-0 left-0 w-full border-b border-edge-default translate-y-0 data-[ending-style]:-translate-y-full data-[starting-style]:-translate-y-full",
}

const POPUP_SIZE_CLASSES: Record<Direction, Record<Size, string>> = {
  right: {
    sm: "w-[320px]",
    md: "w-[400px]",
    lg: "w-[480px]",
    xl: "w-[600px]",
    full: "w-full",
  },
  left: {
    sm: "w-[320px]",
    md: "w-[400px]",
    lg: "w-[480px]",
    xl: "w-[600px]",
    full: "w-full",
  },
  bottom: {
    sm: "h-[30vh]",
    md: "h-[50vh]",
    lg: "h-[70vh]",
    xl: "h-[85vh]",
    full: "h-full",
  },
  top: {
    sm: "h-[30vh]",
    md: "h-[50vh]",
    lg: "h-[70vh]",
    xl: "h-[85vh]",
    full: "h-full",
  },
}

const MINIMIZE_TRANSLATE: Record<Direction, string> = {
  right: "translate-x-full",
  left: "-translate-x-full",
  bottom: "translate-y-full",
  top: "-translate-y-full",
}

const MINIMIZE_TAB_CLASSES: Record<Direction, string> = {
  right: "right-full top-1/2 -translate-y-1/2 rounded-l-md border-y border-l",
  left: "left-full top-1/2 -translate-y-1/2 rounded-r-md border-y border-r",
  bottom: "bottom-full left-1/2 -translate-x-1/2 rounded-t-md border-x border-t",
  top: "top-full left-1/2 -translate-x-1/2 rounded-b-md border-x border-b",
}

const MINIMIZE_ICON: Record<Direction, string> = {
  right: "far fa-chevron-right",
  left: "far fa-chevron-left",
  bottom: "far fa-chevron-down",
  top: "far fa-chevron-up",
}

const RESTORE_ICON: Record<Direction, string> = {
  right: "far fa-chevron-left",
  left: "far fa-chevron-right",
  bottom: "far fa-chevron-up",
  top: "far fa-chevron-down",
}

export interface DrawerProps {
  open?: boolean
  onClose?: () => void
  direction?: Direction
  size?: Size
  modal?: boolean
  /** When true (or returns true), close attempts call onCloseAttempt instead of onClose */
  shouldConfirmBeforeClose?: boolean | (() => boolean)
  /** Called when close is blocked by shouldConfirmBeforeClose */
  onCloseAttempt?: () => void
  /** Enables the minimize tab on the drawer edge */
  minimizable?: boolean
  /** Controlled minimized state */
  minimized?: boolean
  onMinimize?: () => void
  onRestore?: () => void
  /** Enables drag-to-resize (horizontal drawers only) with a persisted width */
  resizable?: boolean
  /** localStorage key the resized width is persisted under */
  widthStorageKey?: string
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
  /** Prevent closing on outside press / focus-out (base-ui passthrough) */
  disablePointerDismissal?: boolean
  className?: string
  children: React.ReactNode
}

const Drawer = ({
  open = false,
  onClose,
  direction = "right",
  size = "md",
  modal = true,
  shouldConfirmBeforeClose,
  onCloseAttempt,
  minimizable = false,
  minimized: controlledMinimized,
  onMinimize,
  onRestore,
  resizable = false,
  widthStorageKey,
  defaultWidth = DEFAULT_RESIZE_WIDTH,
  minWidth = DEFAULT_RESIZE_MIN_WIDTH,
  maxWidth = DEFAULT_RESIZE_MAX_WIDTH,
  disablePointerDismissal,
  children,
  className,
}: DrawerProps) => {
  const [internalMinimized, setInternalMinimized] = React.useState(false)
  const isHorizontal = direction === "right" || direction === "left"
  const enableResize = resizable && isHorizontal
  const isControlled = controlledMinimized !== undefined
  const minimized = isControlled ? controlledMinimized : internalMinimized

  const handleMinimize = () => {
    if (!isControlled) setInternalMinimized(true)
    onMinimize?.()
  }

  const handleRestore = () => {
    if (!isControlled) setInternalMinimized(false)
    onRestore?.()
  }

  const needsCloseGuard =
    typeof shouldConfirmBeforeClose === "function"
      ? shouldConfirmBeforeClose()
      : !!shouldConfirmBeforeClose

  // Reset internal minimized state when drawer closes
  React.useEffect(() => {
    if (!open && !isControlled) {
      setInternalMinimized(false)
    }
  }, [open, isControlled])

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // While minimized, ignore close events — the user is working with the page
      if (minimized) return

      if (needsCloseGuard && typeof onCloseAttempt === "function") {
        onCloseAttempt()
        return
      }

      if (typeof onClose === "function") {
        onClose()
      }
    }
  }

  const popupClassName = cn(
    POPUP_BASE_CLASSES,
    POPUP_DIRECTION_CLASSES[direction],
    minimized && MINIMIZE_TRANSLATE[direction],
    className,
  )

  const popupBody = (
    <>
      {minimizable && (minimized || needsCloseGuard) && (
        <MinimizeTab
          direction={direction}
          minimized={minimized}
          onMinimize={handleMinimize}
          onRestore={handleRestore}
        />
      )}
      <DrawerPrimitive.Content className="flex h-full flex-col overflow-hidden">
        {children}
      </DrawerPrimitive.Content>
    </>
  )

  return (
    <DrawerPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      disablePointerDismissal={disablePointerDismissal}
      // Disable swipe on resizable drawers: it's the same horizontal drag as
      // resize and would fight it.
      swipeDirection={enableResize ? undefined : SWIPE_DIRECTION_MAP[direction]}
      modal={minimized ? false : modal}
    >
      <DrawerPrimitive.Portal keepMounted={minimizable}>
        <DrawerPrimitive.Viewport
          className={cn(
            "fixed inset-0 z-50",
            // Non-modal drawers let clicks fall through; the popup re-enables its own.
            (minimized || !modal) && "pointer-events-none",
          )}
        >
          {enableResize ? (
            <ResizablePopup
              direction={direction}
              className={popupClassName}
              widthStorageKey={widthStorageKey}
              defaultWidth={defaultWidth}
              minWidth={minWidth}
              maxWidth={maxWidth}
            >
              {popupBody}
            </ResizablePopup>
          ) : (
            <DrawerPrimitive.Popup
              className={cn(popupClassName, POPUP_SIZE_CLASSES[direction][size])}
            >
              {popupBody}
            </DrawerPrimitive.Popup>
          )}
        </DrawerPrimitive.Viewport>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  )
}

interface ResizablePopupProps {
  direction: Direction
  className: string
  widthStorageKey?: string
  defaultWidth: number
  minWidth: number
  maxWidth: number
  children: React.ReactNode
}

// Rendered only when resizable, so non-resizable drawers never attach the
// useResizablePanel window listeners.
const ResizablePopup = ({
  direction,
  className,
  widthStorageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  children,
}: ResizablePopupProps) => {
  const popupRef = React.useRef<HTMLDivElement>(null)
  const { width, isResizing, handleResizeStart } = useResizablePanel(popupRef, {
    defaultWidth,
    minWidth,
    maxWidth,
    localStorageKey: widthStorageKey,
    viewportPadding: RESIZE_VIEWPORT_PADDING,
    anchor: direction === "left" ? "left" : "right",
  })

  return (
    <DrawerPrimitive.Popup
      ref={popupRef}
      style={{ width }}
      className={cn(className, isResizing && "select-none")}
    >
      <ResizeHandle
        edge={direction === "left" ? "right" : "left"}
        label="Resize drawer"
        isResizing={isResizing}
        onMouseDown={handleResizeStart}
        // Keep the drag from reaching base-ui's swipe/dismiss handlers.
        onPointerDown={(event) => event.stopPropagation()}
      />
      {children}
    </DrawerPrimitive.Popup>
  )
}

interface MinimizeTabProps {
  direction: Direction
  minimized: boolean
  onMinimize: () => void
  onRestore: () => void
}

const MinimizeTab = ({ direction, minimized, onMinimize, onRestore }: MinimizeTabProps) => {
  return (
    <button
      type="button"
      onClick={minimized ? onRestore : onMinimize}
      aria-label={minimized ? "Restore drawer" : "Minimize drawer"}
      className={cn(
        "pointer-events-auto absolute z-10 flex h-8 w-5 cursor-pointer items-center justify-center border-edge-default bg-surface-default text-content-muted shadow-sm hover:bg-gray-50 hover:text-content-default",
        MINIMIZE_TAB_CLASSES[direction],
      )}
    >
      <i
        className={cn("text-xxs", minimized ? RESTORE_ICON[direction] : MINIMIZE_ICON[direction])}
      />
    </button>
  )
}

export interface DrawerHeaderProps {
  children: React.ReactNode
  className?: string
}

const DrawerHeader = ({ children, className }: DrawerHeaderProps) => {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-between border-b border-edge-default px-4 py-2.5",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      <DrawerPrimitive.Close
        className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:bg-surface-raised hover:text-content-muted"
        aria-label="Close"
      >
        <Icon name="far fa-close" size="sm" />
      </DrawerPrimitive.Close>
    </div>
  )
}

export interface DrawerTitleProps {
  children: React.ReactNode
  className?: string
}

const DrawerTitle = ({ children, className }: DrawerTitleProps) => {
  return (
    <DrawerPrimitive.Title
      className={cn("truncate text-sm font-semibold text-gray-900", className)}
    >
      {children}
    </DrawerPrimitive.Title>
  )
}

export interface DrawerDescriptionProps {
  children?: React.ReactNode
  className?: string
}

const DrawerDescription = ({ children, className }: DrawerDescriptionProps) => {
  return (
    <DrawerPrimitive.Description className={cn("text-sm text-gray-500", className)}>
      {children}
    </DrawerPrimitive.Description>
  )
}

export interface DrawerContentProps {
  children?: React.ReactNode
  className?: string
}

const DrawerContent = ({ children, className }: DrawerContentProps) => {
  return <div className={cn("flex-1 overflow-y-auto p-4", className)}>{children}</div>
}

export interface DrawerFooterProps {
  children: React.ReactNode
  className?: string
}

const DrawerFooter = ({ children, className }: DrawerFooterProps) => {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-end gap-2 border-t border-edge-default bg-surface-raised px-4 py-3",
        className,
      )}
    >
      {children}
    </div>
  )
}

const DrawerClose = DrawerPrimitive.Close

Drawer.Header = DrawerHeader
Drawer.Title = DrawerTitle
Drawer.Description = DrawerDescription
Drawer.Content = DrawerContent
Drawer.Footer = DrawerFooter
Drawer.Close = DrawerClose

export type { Direction as DrawerDirection, Size as DrawerSize }

export default Drawer
